import React from 'react'
import { useContract, useNFTs, useTotalCount } from '@thirdweb-dev/react'
import { BigNumber } from 'ethers'
import { Collection } from '~/domain/Collection'
import { Item } from '~/domain/Item'
import { Vault } from '~/domain/Vault'
import { VaultWithOriginalTokenId, findVaultsInCollection } from '~/components/shared/vault'

interface UseVaultsReturnType {
  data: VaultWithOriginalTokenId[] | undefined
  error: unknown | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * Get id of NFTs that have been fragmented in the given collection
 */
function useVaults (collection: Collection): UseVaultsReturnType {
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [isError, setIsError] = React.useState<boolean>(false)
  const [error, setError] = React.useState<unknown>()
  const [data, setData] = React.useState<VaultWithOriginalTokenId[]>()

  React.useEffect(() => {
    findVaultsInCollection(collection)
      .then((vaults: VaultWithOriginalTokenId[]) => {
        setData(vaults)
      })
      .catch((error: unknown) => {
        setError(error)
        setIsError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [collection])

  return { data, error, isLoading, isError }
}

interface UseItemsReturnType {
  total: BigNumber | undefined
  data: Item[] | undefined
  error: unknown | undefined
  isLoading: boolean
  isError: boolean
}

export function useItems (
  start: number,
  count: number,
  collection: Collection,
  tokenId?: string,
  vault?: Vault
): UseItemsReturnType {
  const isVault = tokenId !== undefined && vault !== undefined
  const { contract } = useContract(
    isVault ? vault.contract.address : collection.contract.address,
    'nft-collection'
  )
  const { data: total, isLoading: isTotalCountLoading } = useTotalCount(contract)
  const { data: nfts, error: collectionError, isLoading: isCollectionLoading, isError: isCollectionError } =
    useNFTs( contract, { start, count })
  const { data: vaults, error: vaultError, isLoading: isVaultLoading, isError: isVaultError } =
    useVaults(collection)
  const isLoading = isTotalCountLoading || isCollectionLoading || isVaultLoading
  const isError = isCollectionError || isVaultError
  const error = isCollectionError
    ? collectionError
    : (
      isVaultError
        ? vaultError
        : undefined
    )

  let items: Item[] | undefined = undefined

  if (!isLoading) {
    if (isVault) {
      // items are fragments
      items = nfts?.map((nft) => {
        return new Item(
          nft,
          collection,
          tokenId,
          vault,
          nft.metadata.id
        )
      })
    } else {
      // items are vaults or full-items
      items = nfts?.map((nft) => {
        const nftVault = vaults?.find((nftVault) => nftVault.tokenId === nft.metadata.id)

        if (nftVault) {
          return new Item(
            nft,
            collection,
            nft.metadata.id,
            nftVault
          )
        }

        return new Item(nft, collection, nft.metadata.id)
      })
    }
  }

  return { total, data: items, error, isLoading, isError }
}
